import { Icon, IconNewsletter } from "@flixbus/honeycomb-icons-react";
import { Heading, ListWrapper, ListWrapperItem, Spinner } from "@flixbus/honeycomb-react";
import {
  addNotification,
  ErrorPage,
  ErrorTypes,
  formatErrorMessage,
  NotificationType,
} from "@flixbus-phx/marketplace-common";
import * as React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Language, PreferenceInput, User } from "../shared/types/schema";
import * as css from "./Settings.scss";
import { useGetUserQuery, useUpdateUserPreferenceMutation } from "./api/operations.generated";
import BookingReportItem from "./ui/bookingReportItem/BookingReportItem";
import ProductNewsItem from "./ui/productNewsItem/ProductNewsItem";
import RideFullyBookedItem from "./ui/rideFullyBookedItem/RideFullyBookedItem";
import ScheduleExpiryItem from "./ui/scheduleExpiryItem/ScheduleExpiryItem";
import SettingsHeader from "./ui/settingsHeader/SettingsHeader";

const Settings: React.FC = () => {
  const intl = useIntl();

  const { data: userData, loading, error } = useGetUserQuery();

  const [updateUserPreference] = useUpdateUserPreferenceMutation({
    onError: (err) =>
      addNotification({
        message: `User settings update failed: ${formatErrorMessage(err.message)}`,
        type: NotificationType.Danger,
      }),
    onCompleted: () => {
      addNotification({
        message: intl.formatMessage({ id: "settings.update.success" }),
      });
    },
  });

  const handleUpdatePreference = (id: User["id"], lang: Language, values: PreferenceInput) => {
    updateUserPreference({
      variables: {
        language: lang,
        preference: {
          ...values,
        },
      },
      optimisticResponse: {
        updateUserPreference: {
          id,
          language: lang,
          preference: {
            ridesFullyBooked: {
              ...values.ridesFullyBooked,
              __typename: "RidesFullyBookedPreference",
            },
            scheduleExpiryReminder: {
              ...values.scheduleExpiryReminder,
              __typename: "ScheduleExpiryReminderPreference",
            },
            productNews: {
              ...values.productNews,
              __typename: "ProductNewsPreference",
            },
            bookingReports: {
              ...values.bookingReports,
              __typename: "BookingReportsPreference",
            },
            __typename: "Preference",
          },
          __typename: "User",
        },
      },
    });
  };

  if (loading) {
    return (
      <div className={css.spinnerWrapper}>
        <Spinner data-testid="spinner" />
      </div>
    );
  }

  if (userData?.getUser) {
    const { id, displayName, mail, language, preference } = userData.getUser;

    return (
      <>
        <SettingsHeader name={displayName} mail={mail || ""} />

        <div data-testid="notification-section">
          <Heading data-testid="notification-header-wrapper" size={3} sectionHeader>
            <Icon InlineIcon={IconNewsletter} extraClasses={css.headerIcon} />
            <FormattedMessage id="settings.notificationSection.header" />
          </Heading>

          <ListWrapper>
            <ListWrapperItem data-testid="daily-booking-report" extraClasses={css.listWrapperItem}>
              <BookingReportItem
                preference={preference.bookingReports}
                onChange={(values) =>
                  handleUpdatePreference(id, language, {
                    ridesFullyBooked: {
                      active: preference.ridesFullyBooked.active,
                      utilization: preference.ridesFullyBooked.utilization,
                    },
                    scheduleExpiryReminder: {
                      active: preference.scheduleExpiryReminder.active,
                    },
                    productNews: {
                      active: preference.productNews.active,
                    },
                    bookingReports: values,
                  })
                }
              />
            </ListWrapperItem>
            <ListWrapperItem data-testid="fully-booked-rides-item" extraClasses={css.listWrapperItem}>
              <RideFullyBookedItem
                preference={preference.ridesFullyBooked}
                onChange={(values) =>
                  handleUpdatePreference(id, language, {
                    ridesFullyBooked: values,
                    scheduleExpiryReminder: {
                      active: preference.scheduleExpiryReminder.active,
                    },
                    productNews: {
                      active: preference.productNews.active,
                    },
                    bookingReports: {
                      active: preference.bookingReports.active,
                      time: preference.bookingReports.time,
                      timezone: preference.bookingReports.timezone,
                    },
                  })
                }
              />
            </ListWrapperItem>
            <ListWrapperItem>
              <ScheduleExpiryItem
                preference={preference.scheduleExpiryReminder}
                onChange={(values) =>
                  handleUpdatePreference(id, language, {
                    ridesFullyBooked: {
                      active: preference.ridesFullyBooked.active,
                      utilization: preference.ridesFullyBooked.utilization,
                    },
                    scheduleExpiryReminder: values,
                    productNews: {
                      active: preference.productNews.active,
                    },
                    bookingReports: {
                      active: preference.bookingReports.active,
                      time: preference.bookingReports.time,
                      timezone: preference.bookingReports.timezone,
                    },
                  })
                }
              />
            </ListWrapperItem>
            <ListWrapperItem>
              <ProductNewsItem
                language={language}
                preference={preference.productNews}
                onChange={(lang, values) =>
                  handleUpdatePreference(id, lang, {
                    ridesFullyBooked: {
                      active: preference.ridesFullyBooked.active,
                      utilization: preference.ridesFullyBooked.utilization,
                    },
                    scheduleExpiryReminder: {
                      active: preference.scheduleExpiryReminder.active,
                    },
                    productNews: values,
                    bookingReports: {
                      active: preference.bookingReports.active,
                      time: preference.bookingReports.time,
                      timezone: preference.bookingReports.timezone,
                    },
                  })
                }
              />
            </ListWrapperItem>
          </ListWrapper>
        </div>
      </>
    );
  }

  if (error) {
    const errorType = error.graphQLErrors?.some(
      (graphqlError) => graphqlError.extensions?.classification === "NOT_FOUND"
    )
      ? ErrorTypes.e403
      : ErrorTypes.e500;

    return <ErrorPage type={errorType} />;
  }

  return <></>;
};

export default Settings;
